/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import React from 'react'

import { subtitle1Css } from '../../../../lib/styles/typography'
import { baseColor, errorTextColor } from '../../../../lib/styles/colors'
import { srOnlyCss } from '../../../../lib/styles/cssStyles'

const labelPositionCss = css`
  top: 0;
  left: 0;
  position: absolute;
  z-index: 1;
  transform: translate(12px, 16px) scale(1);
  transform-origin: top left;
  transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
    transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
`
const labelTransform = css`
  transform: translate(12px, 55%) scale(1);
`
const labelOutlineTransform = css`
  transform: translate(14px, 18px) scale(1);
`
const labelShrinkCss = css`
  transform: translate(11px, 4px) scale(0.75);
`
const labelOutlineShrinkCss = css`
  transform: translate(0, -22px) scale(1);
`

export enum FormLabelShrinkType {
  DEFAULT = 'default',
  OUTLINE = 'outline'
}

const defaultProps = {
  error: false,
  focused: false,
  shrink: true,
  shrinkType: FormLabelShrinkType.DEFAULT,
  srOnly: false
}

export type FormLabelProps = {
  /** ID of associated control */
  associatedId: string
  /** The label text */
  children: React.ReactNode
  /** Indicates field is an error state */
  error?: boolean
  /** If focused, it will use primary color */
  focused?: boolean
  /** If shrunk, it will move the label up, instead of on top of the associated control */
  shrink?: boolean
  /** The style of shrink, be that DEFAULT or OUTLINE */
  shrinkType?: FormLabelShrinkType
  /** Indicates if label should be screen-reader only */
  srOnly?: boolean
} & typeof defaultProps

const FormLabel = ({
  associatedId,
  children,
  error,
  focused,
  shrink,
  shrinkType,
  srOnly
}: FormLabelProps) => {
  const labelCss = css`
    pointer-events: none;
    color: ${error ? errorTextColor : '#6a6a6a'};
  `
  const labelFocusedCss = css`
    color: ${error ? errorTextColor : baseColor};
  `

  const shrinkCss =
    shrinkType === FormLabelShrinkType.OUTLINE ? labelOutlineShrinkCss : labelShrinkCss
  const transformCss =
    shrinkType === FormLabelShrinkType.OUTLINE ? labelOutlineTransform : labelTransform
  const labelCssSets = srOnly
    ? srOnlyCss
    : [
        subtitle1Css,
        labelCss,
        labelPositionCss,
        transformCss,
        focused && labelFocusedCss,
        shrink && shrinkCss
      ]

  return (
    <label css={labelCssSets} htmlFor={associatedId} id={`${associatedId}-label`}>
      {children}
    </label>
  )
}

FormLabel.defaultProps = defaultProps

export default FormLabel
